/**
 * Plain-English summary of a symbol's current state.
 *
 * Every sentence is derived from a number we actually fetched. Nothing about
 * dividends, splits, market cap or company fundamentals appears here — we have
 * no source for any of it, and an invented figure on a finance page is worse
 * than a missing one. Sentences whose input is absent are dropped rather than
 * hedged, so the paragraph is short when the data is thin instead of vague.
 */

function fmtPrice(value, { prefix = '$', points = false } = {}) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
  const num = Number(value);
  const body = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = num < 0 ? '-' : '';
  return points ? `${sign}${body}` : `${sign}${prefix}${body}`;
}

function fmtPct(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
  return `${Math.abs(Number(value)).toFixed(2)}%`;
}

/** Indexes and futures are quoted in points; stocks and ETFs in money. */
export function isPointsQuoted(kind) {
  return kind === 'index' || kind === 'futures';
}

/**
 * @param {object} detail  payload from /api/market-data/symbol/:symbol
 * @returns {string[]} sentences, already ordered
 */
export function buildSymbolSummary(detail) {
  if (!detail?.quote) return [];
  const { symbol, kind, quote, trailing = {} } = detail;
  const points = isPointsQuoted(kind);
  const opts = { prefix: detail.currencyPrefix || '$', points };
  const out = [];

  const last = fmtPrice(quote.lastPrice, opts);
  const pct = fmtPct(quote.percentChange);
  const net = fmtPrice(quote.netChange, opts);

  if (last) {
    if (pct && net && Number.isFinite(Number(quote.percentChange))) {
      const dir = Number(quote.percentChange) >= 0 ? 'higher' : 'lower';
      const verb = Number(quote.percentChange) >= 0 ? 'an increase' : 'a decrease';
      out.push(
        `${symbol} last traded ${dir} at ${last}, ${verb} of ${pct} (${net}) from the previous close.`,
      );
    } else {
      out.push(`${symbol} last traded at ${last}.`);
    }
  }

  // Trailing figures are null when the history does not reach back far enough,
  // which is common for a newly listed symbol. Say only what is covered.
  const month = fmtPct(trailing.monthPct);
  const year = fmtPct(trailing.yearPct);
  if (month && year) {
    out.push(
      `Over the past 30 days it is ${Number(trailing.monthPct) >= 0 ? 'up' : 'down'} ${month}, `
      + `and over the past 12 months ${Number(trailing.yearPct) >= 0 ? 'up' : 'down'} ${year}.`,
    );
  } else if (month) {
    out.push(`Over the past 30 days it is ${Number(trailing.monthPct) >= 0 ? 'up' : 'down'} ${month}.`);
  }

  const yearLow = fmtPrice(quote.yearLow, opts);
  const yearHigh = fmtPrice(quote.yearHigh, opts);
  if (yearLow && yearHigh && Number.isFinite(Number(quote.yearRangePosition))) {
    out.push(
      `It sits ${Math.round(Number(quote.yearRangePosition))}% of the way up its 52-week range `
      + `of ${yearLow} to ${yearHigh}.`,
    );
  }

  if (detail.exchange) {
    out.push(`Quoted on ${detail.exchange}${detail.currency ? ` in ${detail.currency}` : ''}.`);
  }

  return out;
}

/**
 * The caveat that has to travel with these numbers.
 *
 * Delayed data presented without saying so is the kind of thing a reader acts
 * on. Price-return-only matters for the same reason it does on the benchmark
 * cards: a dividend payer looks worse than it was.
 */
export function symbolDisclaimer(kind) {
  const base = 'Delayed market data, for information only — not a quote you can trade on.';
  if (isPointsQuoted(kind)) {
    return `${base} Levels are index points, not dollars.`;
  }
  return `${base} Price return only; dividends and distributions are excluded.`;
}
